import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
  Download as DownloadIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInFull as OpenInFullIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { gradients, surface } from '../../theme';
import ThumbnailService from '../../services/ThumbnailService';
import { triggerBrowserDownload } from '../../utils/download';

const GENERATIONS_PER_PAGE = 10;
const POLL_INTERVAL_MS = 4000;
const PENDING_STATUSES = ['queued', 'running'];

// Thumbnails are always 16:9 and shown four to a row.
const IMAGE_ASPECT_RATIO = '16 / 9';
const IMAGES_PER_ROW = 4;

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatUsd(amount) {
  return usd.format(amount || 0);
}

function imageCountLabel(count) {
  return count === 1 ? '1 image' : `${count.toLocaleString()} images`;
}

function hasPendingImages(generations) {
  return generations.some((generation) =>
    generation.images.some((image) => PENDING_STATUSES.includes(image.status)),
  );
}

function downloadFilename(generation, image) {
  const promptPart =
    generation.prompt
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'thumbnail';
  let extension = 'jpg';
  try {
    extension = new URL(image.imageUrl).pathname.split('.').pop() || extension;
  } catch (_) {
    // Keep the default extension.
  }
  return `${promptPart}_${image.position + 1}.${extension}`;
}

function formatCreatedAt(value) {
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function OptionLabel({ children }) {
  return (
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
      {children}
    </Typography>
  );
}

const toggleGroupSx = {
  flexWrap: 'wrap',
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px !important',
    m: 0,
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    px: 1.25,
    py: 0.5,
    gap: 0.75,
    bgcolor: 'background.paper',
  },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    borderColor: 'primary.main',
  },
  '& .MuiToggleButton-root.Mui-selected:hover': { bgcolor: 'primary.dark' },
};

function GeneratePanel({
  options,
  prompt,
  onPromptChange,
  count,
  onCountChange,
  resolution,
  onResolutionChange,
  onGenerate,
  generating,
}) {
  const selectedResolution = options?.resolutions.find((option) => option.resolution === resolution);
  const canGenerate = Boolean(selectedResolution && prompt.trim()) && !generating;

  function handlePromptKeyDown(event) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && canGenerate) {
      event.preventDefault();
      onGenerate();
    }
  }

  const countOptions = Array.from({ length: options?.maxImagesPerGeneration || 4 }, (_, index) => index + 1);
  const generationCost = (selectedResolution?.pricePerImageUsd || 0) * count;

  return (
    <Paper
      sx={{
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        p: { xs: 2, md: 2.5 },
        position: { md: 'sticky' },
        top: { md: 16 },
        display: 'grid',
        gap: 2.25,
      }}
    >
      <Box>
        <OptionLabel>Model</OptionLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          <Chip
            icon={<AutoAwesomeIcon />}
            label={options?.model?.name || 'Seedream 5.0 Pro'}
            sx={{ fontWeight: 600, background: gradients.primarySubtle }}
          />
          {selectedResolution && (
            <Typography variant="caption" color="text.secondary">
              {options.aspectRatio} · {selectedResolution.resolution} ({selectedResolution.size.replace('x', ' × ')})
            </Typography>
          )}
        </Box>
      </Box>

      <TextField
        label="Prompt"
        placeholder="Describe the thumbnail you want: the subject, setting, mood, lighting, and any text on it."
        multiline
        minRows={5}
        maxRows={8}
        value={prompt}
        onChange={(event) => onPromptChange(event.target.value)}
        onKeyDown={handlePromptKeyDown}
        inputProps={{ maxLength: options?.maxPromptLength }}
        helperText="Ctrl + Enter to generate"
        sx={{ bgcolor: 'background.paper' }}
      />

      <Box>
        <OptionLabel>Images</OptionLabel>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={count}
          onChange={(event, value) => value !== null && onCountChange(value)}
          aria-label="Number of images"
          sx={toggleGroupSx}
        >
          {countOptions.map((option) => (
            <ToggleButton key={option} value={option} sx={{ minWidth: 44 }}>
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box>
        <OptionLabel>Resolution</OptionLabel>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={resolution}
          onChange={(event, value) => value !== null && onResolutionChange(value)}
          aria-label="Image resolution"
          sx={toggleGroupSx}
        >
          {(options?.resolutions || []).map((option) => (
            <ToggleButton key={option.resolution} value={option.resolution} sx={{ minWidth: 88 }}>
              {option.resolution} · {formatUsd(option.pricePerImageUsd)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Button
          fullWidth
          size="large"
          variant="contained"
          startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          disabled={!canGenerate}
          onClick={onGenerate}
          sx={{ background: canGenerate ? gradients.primary : undefined, py: 1.25 }}
        >
          Generate {imageCountLabel(count)}
        </Button>
        {selectedResolution && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
            {formatUsd(generationCost)} ({formatUsd(selectedResolution.pricePerImageUsd)} per image)
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/** Today's and all-time cost of the user's thumbnails, deleted ones included. */
function CostSummary({ costs }) {
  const tiles = [
    { label: 'Today', summary: costs?.today },
    { label: 'Total so far', summary: costs?.total },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      {tiles.map(({ label, summary }) => (
        <Paper
          key={label}
          sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2, px: 2, py: 1.25, minWidth: 140 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
            {costs ? formatUsd(summary.costUsd) : '–'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {costs ? imageCountLabel(summary.images) : ' '}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function ImageTile({ generation, image, onOpen, onRetry, onDelete }) {
  const pending = PENDING_STATUSES.includes(image.status);
  const completed = image.status === 'completed' && image.imageUrl;

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: IMAGE_ASPECT_RATIO,
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'rgba(102, 126, 234, 0.08)',
        border: '1px solid rgba(102, 126, 234, 0.15)',
        '&:hover .tile-actions, &:focus-within .tile-actions': { opacity: 1 },
      }}
    >
      {completed && (
        <Box
          component="img"
          src={image.imageUrl}
          alt={generation.prompt}
          loading="lazy"
          onClick={() => onOpen(image)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
        />
      )}

      {pending && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: 'primary.main',
            background:
              'linear-gradient(110deg, rgba(102,126,234,0.06) 30%, rgba(118,75,162,0.16) 50%, rgba(102,126,234,0.06) 70%)',
            backgroundSize: '250% 100%',
            animation: 'thumbnailShimmer 1.6s linear infinite',
            '@keyframes thumbnailShimmer': {
              from: { backgroundPosition: '100% 0' },
              to: { backgroundPosition: '-150% 0' },
            },
          }}
        >
          <CircularProgress size={26} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {image.status === 'queued' ? 'Queued' : 'Generating'}
          </Typography>
        </Box>
      )}

      {image.status === 'failed' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 2,
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon color="error" />
          <Typography
            variant="caption"
            color="error"
            sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            title={image.error}
          >
            {image.error || 'Generation failed'}
          </Typography>
          <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={() => onRetry(image)}>
            Retry
          </Button>
        </Box>
      )}

      {!pending && (
        <Box
          className="tile-actions"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 0.5,
            // Shown on hover so they do not cover the small tiles; on a phone,
            // tapping an image opens the viewer, which can download or delete it.
            opacity: 0,
            transition: 'opacity 120ms ease',
            '& .MuiIconButton-root': {
              bgcolor: 'rgba(15, 18, 40, 0.6)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(15, 18, 40, 0.8)' },
            },
          }}
        >
          {completed && (
            <>
              <Tooltip title="View full size">
                <IconButton size="small" aria-label="View full size" onClick={() => onOpen(image)}>
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  aria-label="Download"
                  onClick={() => triggerBrowserDownload(image.imageUrl, downloadFilename(generation, image))}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Delete image">
            <IconButton size="small" aria-label="Delete image" onClick={() => onDelete(image)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

function GenerationCard({ generation, onReusePrompt, onOpenImage, onRetryImage, onDeleteImage, onDeleteGeneration }) {

  return (
    <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2, p: { xs: 1.5, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            title={generation.prompt}
            sx={{ fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {generation.prompt}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
            <Chip size="small" label={imageCountLabel(generation.images.length)} />
            <Chip size="small" label={generation.resolution} />
            {generation.costUsd > 0 && <Chip size="small" label={formatUsd(generation.costUsd)} />}
            <Typography variant="caption" color="text.secondary">
              {formatCreatedAt(generation.createdAt)}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Use this prompt again">
          <IconButton size="small" aria-label="Use this prompt" onClick={() => onReusePrompt(generation)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete all images in this generation">
          <IconButton size="small" aria-label="Delete generation" onClick={() => onDeleteGeneration(generation)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${IMAGES_PER_ROW}, minmax(0, 1fr))`,
          gap: { xs: 0.75, sm: 1.25 },
        }}
      >
        {generation.images.map((image) => (
          <ImageTile
            key={image.id}
            generation={generation}
            image={image}
            onOpen={(openedImage) => onOpenImage(generation, openedImage)}
            onRetry={onRetryImage}
            onDelete={onDeleteImage}
          />
        ))}
      </Box>
    </Paper>
  );
}

function ImageViewer({ opened, onClose, onDelete }) {
  const { generation, image } = opened || {};
  return (
    <Dialog
      open={Boolean(opened)}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { bgcolor: 'rgba(12, 14, 30, 0.96)', borderRadius: 2, m: { xs: 1, md: 4 } } }}
    >
      {opened && (
        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={image.imageUrl}
            alt={generation.prompt}
            sx={{ display: 'block', maxWidth: 'calc(100vw - 64px)', maxHeight: 'calc(100vh - 140px)' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, color: 'white' }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, opacity: 0.85 }} noWrap title={generation.prompt}>
              {generation.prompt}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => triggerBrowserDownload(image.imageUrl, downloadFilename(generation, image))}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => onDelete(image)}
            >
              Delete
            </Button>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Dialog>
  );
}

function EmptyFeed() {
  return (
    <Paper
      sx={{
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        p: 5,
        textAlign: 'center',
        color: 'text.secondary',
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
      <Typography variant="h6" color="text.primary">
        No thumbnails yet
      </Typography>
      <Typography variant="body2">
        Write a prompt, pick how many images you want, then generate. Your images show up here.
      </Typography>
    </Paper>
  );
}

export default function ThumbnailsPage() {
  const [options, setOptions] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(1);
  // Set to the server's default resolution once the options load.
  const [resolution, setResolution] = useState('');
  const [generations, setGenerations] = useState([]);
  const [costs, setCosts] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(GENERATIONS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [opened, setOpened] = useState(null);
  const limitRef = useRef(limit);
  limitRef.current = limit;

  const loadGenerations = useCallback(async (requestedLimit) => {
    const data = await ThumbnailService.list(requestedLimit);
    // Ignore a slow response for a page size the user has since changed.
    if (requestedLimit !== limitRef.current) return;
    setGenerations(data.generations);
    setHasMore(data.hasMore);
    setCosts(data.costs);
  }, []);

  useEffect(() => {
    ThumbnailService.getOptions()
      .then((loadedOptions) => {
        setOptions(loadedOptions);
        setResolution(loadedOptions.defaultResolution);
      })
      .catch((err) => setError(err.response?.data?.error || 'Could not load the image options.'));
  }, []);

  useEffect(() => {
    setLoading(true);
    loadGenerations(limit)
      .catch((err) => setError(err.response?.data?.error || 'Could not load your thumbnails.'))
      .finally(() => setLoading(false));
  }, [limit, loadGenerations]);

  // Refresh while any image is still generating; costs update as images complete.
  const pending = hasPendingImages(generations);
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(() => {
      loadGenerations(limitRef.current).catch(() => {
        // Try again on the next tick.
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pending, loadGenerations]);

  async function generate() {
    setGenerating(true);
    setError('');
    try {
      const generation = await ThumbnailService.generate({ prompt: prompt.trim(), count, resolution });
      setGenerations((current) => [generation, ...current]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start generating.');
    } finally {
      setGenerating(false);
    }
  }

  async function runAndReload(action, failureMessage) {
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.response?.data?.error || failureMessage);
    }
    await loadGenerations(limitRef.current).catch(() => {});
  }

  function reusePrompt(generation) {
    setPrompt(generation.prompt);
    setResolution(generation.resolution);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function deleteImage(image, onConfirmed) {
    if (!window.confirm('Delete this image? What it cost still counts.')) return;
    onConfirmed?.();
    runAndReload(() => ThumbnailService.deleteImage(image.id), 'Could not delete the image.');
  }

  function deleteGeneration(generation) {
    const imageCount = generation.images.length;
    const question = imageCount === 1 ? 'Delete this image?' : `Delete all ${imageCount} images in this generation?`;
    if (!window.confirm(`${question} What they cost still counts.`)) return;
    runAndReload(() => ThumbnailService.deleteGeneration(generation.id), 'Could not delete the generation.');
  }

  function retryImage(image) {
    runAndReload(() => ThumbnailService.retryImage(image.id), 'Could not retry the image.');
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
            Thumbnails
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Generate 16:9 1K or 2K images from a prompt with Seedream 5.0 Pro, up to four at a time.
          </Typography>
        </Box>
        <CostSummary costs={costs} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '360px minmax(0, 1fr)' },
          alignItems: 'start',
          gap: 2,
        }}
      >
        <GeneratePanel
          options={options}
          prompt={prompt}
          onPromptChange={setPrompt}
          count={count}
          onCountChange={setCount}
          resolution={resolution}
          onResolutionChange={setResolution}
          onGenerate={generate}
          generating={generating}
        />

        <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          {loading && generations.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}
          {!loading && generations.length === 0 && <EmptyFeed />}
          {generations.map((generation) => (
            <GenerationCard
              key={generation.id}
              generation={generation}
              onReusePrompt={reusePrompt}
              onOpenImage={(openedGeneration, image) => setOpened({ generation: openedGeneration, image })}
              onRetryImage={retryImage}
              onDeleteImage={deleteImage}
              onDeleteGeneration={deleteGeneration}
            />
          ))}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="inherit"
                startIcon={loading ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                disabled={loading}
                onClick={() => setLimit((current) => current + GENERATIONS_PER_PAGE)}
                sx={{ bgcolor: surface.glass }}
              >
                Load older generations
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <ImageViewer
        opened={opened}
        onClose={() => setOpened(null)}
        onDelete={(image) => deleteImage(image, () => setOpened(null))}
      />
    </Box>
  );
}
